"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createSmartFloat = exports.SmartFloat = exports.RoundingMode = exports.isSmartFloat = exports.isSafeFloat = exports.MathUtility = void 0;
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const error_1 = require("./error");
class MathUtility {
    static highestPower(n, base, options = { inclusive: true }) {
        if (n < 1) {
            return null;
        }
        let p = 0;
        while (options.inclusive ? Math.pow(base, p + 1) <= n : Math.pow(base, p + 1) < n) {
            p += 1;
        }
        return p;
    }
    static toBinaryString(n) {
        let s = '';
        let highestPower = MathUtility.highestPower(n, 2);
        while (highestPower >= 0) {
            const highestTwoPower = Math.pow(2, highestPower);
            if (n >= highestTwoPower) {
                s += '1';
                n -= highestTwoPower;
            }
            else {
                s += '0';
            }
            highestPower -= 1;
        }
        return s;
    }
}
exports.MathUtility = MathUtility;
/** Typeguard checking if value is a SafeFloat */
const isSafeFloat = (input) => {
    return (typeof input === 'object' &&
        Number.isFinite(input.amount) &&
        Number.isFinite(input.precision) &&
        input.amount != undefined &&
        input.precision != undefined);
};
exports.isSafeFloat = isSafeFloat;
/** Typeguard checking if value is a SmartFloat */
const isSmartFloat = (input) => {
    return input.toSafeFloat != null;
};
exports.isSmartFloat = isSmartFloat;
var RoundingMode;
(function (RoundingMode) {
    RoundingMode[RoundingMode["RoundUp"] = bignumber_js_1.default.ROUND_UP] = "RoundUp";
    RoundingMode[RoundingMode["RoundDown"] = bignumber_js_1.default.ROUND_DOWN] = "RoundDown";
    RoundingMode[RoundingMode["Ceil"] = bignumber_js_1.default.ROUND_CEIL] = "Ceil";
    RoundingMode[RoundingMode["Floor"] = bignumber_js_1.default.ROUND_FLOOR] = "Floor";
    RoundingMode[RoundingMode["RoundHalfUp"] = bignumber_js_1.default.ROUND_HALF_UP] = "RoundHalfUp";
    RoundingMode[RoundingMode["RoundHalfDown"] = bignumber_js_1.default.ROUND_HALF_DOWN] = "RoundHalfDown";
    RoundingMode[RoundingMode["RoundHalfEven"] = bignumber_js_1.default.ROUND_HALF_EVEN] = "RoundHalfEven";
    RoundingMode[RoundingMode["RoundHalfCeil"] = bignumber_js_1.default.ROUND_HALF_CEIL] = "RoundHalfCeil";
    RoundingMode[RoundingMode["RoundHalfFloor"] = bignumber_js_1.default.ROUND_HALF_FLOOR] = "RoundHalfFloor";
})(RoundingMode = exports.RoundingMode || (exports.RoundingMode = {}));
/**
 *
 * @desc Utility that ease floating point calculation, it uses SafeFloat graphql interface.
 *
 */
class SmartFloat {
    /**
     * Construct a "SmartFloat" (deserialized 'safe' float value)
     * - protects against infamous float math bugs in javascript
     * - offers a variety of built-in tooling (math, comparisons, precision manipulation, serialization options)
     *
     * @throws if given an incorrectly formatted 'safe' float (i.e. safe float interface but with non-integer values)
     * */
    constructor(input) {
        if (exports.isSafeFloat(input)) {
            if (!Number.isInteger(input.amount) || !Number.isInteger(input.precision)) {
                throw new error_1.CommonUtilsError('Cannot construct SmartFloat from incorrectly formatted "SafeFloat" input: amount and precision must be integers.', { input });
            }
            this._currentDecimalPrecision = input.precision;
            // If the input is a SafeFloat, we need to convert the number by doing: number / (10 ^ precision).
            this._number = new bignumber_js_1.default(input.amount).shiftedBy(input.precision * -1);
        }
        else if (input instanceof SmartFloat) {
            // If the input is an instance of SmartFloat, then create a new number based on the input primitive number.
            this._currentDecimalPrecision = input.getDecimalPrecision();
            this._number = new bignumber_js_1.default(input.toNumber());
        }
        else {
            this._number = new bignumber_js_1.default(input);
            if (this._number.isNaN()) {
                throw new error_1.CommonUtilsError('Input value must be a pure number representation, without locale formatting.', {
                    input
                });
            }
            this._currentDecimalPrecision = this._number.decimalPlaces();
        }
    }
    /**
     * @desc Private function that converts a FloatInput type to a primitive number.
     * @param {number|string|SafeFloat|SmartFloat}input
     */
    static inputToNumber(input) {
        if (input instanceof SmartFloat) {
            // If the input is an instance of SmartFloat, then return a primitive number.
            return input.toNumber();
        }
        else if (exports.isSafeFloat(input)) {
            // If we determine the input is a SafeFloat, then create an SmartFloat and return the primitive number.
            return new SmartFloat(input).toNumber();
        }
        // Strings and numbers don't need any processing.
        return input;
    }
    /** @returns new SmartFloat value representing this value plus input. Does not mutate this value or input. */
    add(addend) {
        return new SmartFloat(this._number.plus(SmartFloat.inputToNumber(addend)).toNumber());
    }
    /** @returns new SmartFloat value representing this value minus input. Does not mutate this value or input. */
    subtract(subtrahend) {
        return new SmartFloat(this._number.minus(SmartFloat.inputToNumber(subtrahend)).toNumber());
    }
    /** @returns new SmartFloat value representing this value multiplied by input. Does not mutate this value or input.*/
    multiply(multiplier) {
        return new SmartFloat(this._number.multipliedBy(SmartFloat.inputToNumber(multiplier)).toNumber());
    }
    /** @returns new SmartFloat value representing this value divided by input. Does not mutate this value or input.*/
    divide(divisor) {
        return new SmartFloat(this._number.dividedBy(SmartFloat.inputToNumber(divisor)).toNumber());
    }
    /**
     * Returns this value to the n power, with possibility of curtailing precision via modulus m arg.
     * Does not mutate this value or input.
     *
     * @param n — The exponent, an integer.
     * @param m — An optional modulus.
     * @returns new SmartFloat value representing result of calculation */
    pow(n, m) {
        return new SmartFloat(this._number.pow(SmartFloat.inputToNumber(n), m ? SmartFloat.inputToNumber(m) : undefined).toNumber());
    }
    /** @returns boolean indicating whether this value is greater than [>] input value. Does not mutate this value or input.*/
    isGreaterThan(comparand) {
        return this._number.isGreaterThan(SmartFloat.inputToNumber(comparand));
    }
    /** @returns boolean indicating whether this value is greater than or equal to [>=] input value. Does not mutate this value or input.*/
    isGreaterThanOrEqualTo(comparand) {
        return this._number.isGreaterThanOrEqualTo(SmartFloat.inputToNumber(comparand));
    }
    /** @returns boolean indicating whether this value is less than [<] input value. Does not mutate this value or input.*/
    isLessThan(comparand) {
        return this._number.isLessThan(SmartFloat.inputToNumber(comparand));
    }
    /** @returns boolean indicating whether this value is less than or equal to [<=] input value. Does not mutate this value or input.*/
    isLessThanOrEqualTo(comparand) {
        return this._number.isLessThanOrEqualTo(SmartFloat.inputToNumber(comparand));
    }
    /** @returns square root of this value as new SmartFloat. Does not mutate this value. */
    sqrt() {
        return new SmartFloat(this._number.sqrt().toNumber());
    }
    /** @returns this value multiple by negative 1 as new SmartFloat. Does not mutate this value. */
    negate() {
        return new SmartFloat(this._number.negated().toNumber());
    }
    /** @returns boolean indicating whether this value is equal to zero. Does not mutate this value. */
    isZero() {
        return this._number.isZero();
    }
    /** @returns boolean indicating whether this value is equal to [===] input value. Does not mutate this value or input.*/
    isEqualTo(comparand) {
        return this._number.isEqualTo(SmartFloat.inputToNumber(comparand));
    }
    /** @returns integer representing number of digits of precision past the decimal point this value tracks. */
    getDecimalPrecision() {
        return this._currentDecimalPrecision;
    }
    /**
     * Creates a new SmartFloat with value equal to current smart float but with decimal precision requested
     * (rounding if necessary).
     *
     * @param targetPrecision An integer representing number of digits of precision past decimal point requested
     * @param roundingMode Optional RoundingMode indicating how to round off trailing digits if precision is shortened
     * NOTE: default rounding style is Banker's Rounding, a.k.a. "RoundHalfEven"
     * @returns a new SmartFloat object adjusted to the requested precision and rounding style
     */
    toPrecision(targetPrecision, roundingMode = RoundingMode.RoundHalfEven) {
        if (!Number.isInteger(targetPrecision)) {
            throw new error_1.CommonUtilsError('Cannot convert decimal precision: requested precision must be an integer', {
                targetPrecision,
                currentValue: this.serialize()
            });
        }
        const amount = this._number
            .shiftedBy(targetPrecision)
            .integerValue(roundingMode)
            .toNumber();
        return new SmartFloat({ amount, precision: targetPrecision });
    }
    /**
     * WARNING: javascript number handling for floats is inherent "unsafe" (a.k.a. notoriously buggy);
     * AVOID CALLING THIS method whenever possible.
     * @returns this value as plain javascript number primitive.
     */
    toNumber() {
        return this._number.toNumber();
    }
    /**
     * Recommended serialization form over plain javascript numbers for "safe" float handling
     * @returns this value serialized into "SafeFloat" JSON form { amount: integer, precision: integer }
     */
    toSafeFloat(maxDecimals, roundingMode = RoundingMode.RoundHalfEven) {
        if (maxDecimals != null || roundingMode !== RoundingMode.RoundHalfEven) {
            return this.toPrecision(maxDecimals, roundingMode).toSafeFloat();
        }
        const amount = this._number
            // Shift the number based on the amount of decimals, this is equivalent to number * 10 ^ precision
            // This is needed to remove the decimal part of a number.
            .shiftedBy(this._currentDecimalPrecision)
            // Convert the number to an integer using an specific rounding strategy.
            .integerValue(roundingMode)
            // We return a primitive number.
            .toNumber();
        return {
            amount,
            precision: this._currentDecimalPrecision
        };
    }
    /**
     * Alternate "safe" float form using straight string serialization for float values.
     * Still strongly preferred over plain javascript numbers for "safe" float handling.
     *
     * NOTE: Includes trailing zeroes padded out to internal "current" precision
     *
     * @returns this value serialized into "StringFloat" JSON form, e.g. `"12.345"`
     */
    toStringFloat() {
        const maybeTrimmedFloatString = this._number.toString(10);
        // If we have an integer, `fraction` will be undefined. Default to empty string to retain padding.
        const [whole, fraction = ''] = maybeTrimmedFloatString.split('.');
        const paddedFraction = fraction.padEnd(this._currentDecimalPrecision, '0');
        // If the currentDecimalPrecision is zero (or negative), then we will not include any fractional parts.
        return this._currentDecimalPrecision <= 0 ? whole : [whole, paddedFraction].join('.');
    }
    /**
     * Recommended serialization form over plain javascript numbers for "safe" float handling
     * @returns this value serialized into "SafeFloat" JSON form { amount: integer, precision: integer }
     */
    serialize() {
        return this.toSafeFloat();
    }
}
exports.SmartFloat = SmartFloat;
function createSmartFloat(input) {
    return new SmartFloat(input);
}
exports.createSmartFloat = createSmartFloat;
//# sourceMappingURL=math.js.map